import formatISO from 'date-fns/formatISO'
import { axiosApi } from './api'
import { providesList } from 'utils/rtkHelpers'
import { transformCollectionFiles, transformSharedFilesWithSummary } from 'utils/transform'
import {
    patchCollection,
    patchFileCount,
    getCollections,
    addOne,
} from 'features/collections-list/collectionSlice'
import {
    invalidateIndicators,
    getFileIndicators,
    recacheIndicators,
} from 'features/patient-file-thumbnail/thumbnailSlice'

const type = 'Collection'

export const collection = axiosApi.injectEndpoints({
    endpoints: build => ({
        /*
         * GET list of collections under an agreement
         */
        getCollections: build.query({
            query: () => `v1/collections?Sort.Desc=true`,
            transformResponse: res => res.data,
            providesTags: result => providesList(undefined, type),
            keepUnusedDataFor: 3600,
        }),

        /*
         * GET list of files under a collections
         */
        getCollectionFiles: build.query({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const summary = await fetchWithBQ(
                    `v1/collections/${_arg.id}/files?Summary=count&${_arg.query}`
                )
                if (summary.error) throw summary.error
                const count = summary.data.summary

                if (count.all) {
                    const result = await fetchWithBQ(
                        `v1/collections/${_arg.id}/files?${_arg.query}`
                    )
                    if (!result.data) {
                        return { error: result.error }
                    }
                    return {
                        data: {
                            files: transformCollectionFiles(result.data.data),
                            summary: count,
                        },
                    }
                }
                return {
                    data: {
                        files: [],
                        summary: count,
                    },
                }
            },

            /*
             * Update collection list with updated file count in case someone else from
             * the same agreement has deleted/added any files in between for the same collection.
             */
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(patchCollection({ id, fileCount: data.summary.all }))
                } catch {}
            },
            providesTags: (result, error, arg) =>
                result ? ['CollectionFiles', { type: 'CollectionFiles', id: arg.id }] : null,
            keepUnusedDataFor: 1200,
        }),

        /*
         * GET summary of the shared files
         */
        getSharedSummary: build.query({
            query: () => `v1/share/$summary`,
            transformResponse: res => res.data,
            providesTags: result => (result ? ['SharedSummary'] : null),
            keepUnusedDataFor: 3600,
        }),

        /*
         * GET files and summary of a patient by patient id
         */
        getSharedFilesWithSummary: build.query({
            async queryFn(_arg, { dispatch }, _extraOptions, fetchWithBQ) {
                const summary = await fetchWithBQ(`v1/share/$summary`)
                if (summary.error) throw summary.error
                const count = summary.data.data

                if (count.all) {
                    const result = await fetchWithBQ(
                        `v1/share/sharedfiles?timezone=${new Date().getTimezoneOffset()}`
                    )
                    if (!result.data) {
                        return { error: result.error }
                    }

                    dispatch(
                        axiosApi.util.updateQueryData('getSharedSummary', undefined, draft => {
                            Object.assign(draft, count)
                        })
                    )

                    return {
                        data: transformSharedFilesWithSummary({ data: result.data.data, count }),
                    }
                }
                return {
                    data: {
                        grouped: [],
                        files: [],
                        summary: count,
                    },
                }
            },
            providesTags: result => (result ? ['SharedFiles'] : null),
            keepUnusedDataFor: 1200,
        }),

        /*
         * Add a new collection to the agreement
         */
        addCollection: build.mutation({
            query: body => ({
                url: 'v1/collections',
                method: 'POST',
                body,
            }),
            transformResponse: res => res.data,
            async onQueryStarted(body, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        axiosApi.util.updateQueryData('getCollections', undefined, draft => {
                            draft.unshift({ ...data, isNew: true })
                        })
                    )
                    dispatch(addOne(data))
                } catch {}
            },
            invalidatesTags: result => (result ? ['DisabledState'] : null),
        }),

        /*
         * Add files to collections
         */
        addFilesToCollection: build.mutation({
            query: body => ({
                url: 'v1/collections/_associate',
                method: 'PUT',
                body,
            }),
            transformResponse: res => res.data,
            async onQueryStarted({ collectionIds, fileIds }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled

                    /*
                     * Update collection list with reduced file count and updated datetime
                     */
                    if (fileIds) {
                        await dispatch(
                            patchFileCount({
                                id: collectionIds.split(','),
                                fileCount: fileIds.split(',').length,
                                updatedDateTime: formatISO(new Date()),
                            })
                        )
                        await dispatch(invalidateIndicators(fileIds.split(',')))
                        dispatch(getFileIndicators({ ids: fileIds }))
                    } else {
                        dispatch(getCollections())
                        dispatch(recacheIndicators())
                    }
                } catch {}
            },
            invalidatesTags: (result, error, arg) =>
                result
                    ? [
                          'DisabledState',
                          ...arg.collectionIds.split(',').map(id => {
                              return {
                                  id,
                                  type: 'CollectionFiles',
                              }
                          }),
                      ]
                    : null,
        }),

        /*
         * Delete files from collections
         */
        deleteFilesFromCollection: build.mutation({
            query: ({ id, body }) => ({
                url: `v1/collections/${id}/_removefiles`,
                method: 'PUT',
                body,
            }),
            transformResponse: res => res.data,
            async onQueryStarted({ id, body, query, summary }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled

                    /*
                     * Update collection list with reduced file count and updated datetime
                     */
                    await dispatch(
                        patchCollection({
                            id,
                            fileCount: data.fileCount,
                            updatedDateTime: data.updatedDateTime,
                        })
                    )
                    await dispatch(invalidateIndicators(body.fileIds.split(',')))
                    dispatch(getFileIndicators({ ids: body.fileIds }))

                    /*
                     * Update cache by removing the deleted fileIds from the list to avoid refetch
                     * and subtracting the summary with file types
                     */
                    dispatch(
                        axiosApi.util.updateQueryData(
                            'getCollectionFiles',
                            { id, query },
                            draft => {
                                draft.files = draft.files.filter(
                                    item => !body.fileIds.split(',').includes(item.fileId)
                                )
                                draft.summary = summary
                            }
                        )
                    )
                } catch {}
            },
            invalidatesTags: result => (result ? ['DisabledState'] : null),
        }),

        /*
         * GET disabled collections.
         */
        getDisabledCollections: build.query({
            query: query => `v1/collections/_whenallfiles?${query}`,
            transformResponse: res => res.data,
            providesTags: result => (result ? ['DisabledState'] : null),
            keepUnusedDataFor: 3600,
        }),

        /*
         * GET auto suggestion for collections search.
         */
        getCollectionsSuggestions: build.query({
            async queryFn({ keyword, query = '' }, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ(`v1/collections?Query=name=co:"${keyword}"&${query}`)
                if (res.error) throw res.error
                const collections = res.data.data

                const result = await fetchWithBQ(
                    `v1/collections/_tags?Query=tags=co:"${keyword}"&${query}`
                )

                const tags = result.data.data
                const response = []
                if (collections.length > 0) {
                    response.push({
                        type: 'folder',
                        data: collections.map(c => {
                            return {
                                id: c.id,
                                name: c.name,
                            }
                        }),
                    })
                }
                if (tags.length > 0) {
                    response.push({
                        type: 'tag',
                        data: tags.map((t, i) => {
                            return {
                                id: i + 1,
                                name: t,
                            }
                        }),
                    })
                }
                return {
                    data: response,
                }
            },
            transformResponse: res => res.data,
            providesTags: result => (result ? ['CollectionSuggestions'] : null),
            keepUnusedDataFor: 600,
        }),

        /*
         * GET list of favorite files
         */
        getFavoriteFiles: build.query({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const summary = await fetchWithBQ(
                    `v1/favorites/${_arg.id}/files?Summary=count&${_arg.query}`
                )
                if (summary.error) throw summary.error
                const count = summary.data.summary

                if (count.all) {
                    const result = await fetchWithBQ(`v1/favorites/${_arg.id}/files?${_arg.query}`)
                    if (!result.data) {
                        return { error: result.error }
                    }
                    return {
                        data: {
                            files: result.data.data,
                            summary: count,
                        },
                    }
                }
                return {
                    data: {
                        files: [],
                        summary: count,
                    },
                }
            },

            /*
             * Update favorites list with updated file count in case someone else from
             * the same agreement has deleted/added any files in between for the same favorites.
             */
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(patchCollection({ id, fileCount: data.summary.all }))
                } catch {}
            },
            providesTags: (result, error, arg) =>
                result ? ['FavoriteFiles', { type: 'FavoriteFiles', id: arg.id }] : null,
            keepUnusedDataFor: 1200,
        }),

        /*
         * GET summary of the collections
         */
        getCollectionsSummary: build.query({
            query: ({ id, query = '' }) => `v1/collections/${id}/files?Summary=count&${query}`,
            transformResponse: res => res.summary,
            keepUnusedDataFor: 600,
        }),
    }),

    overrideExisting: true,
})

export const {
    useGetCollectionsQuery,
    useGetCollectionFilesQuery,
    useGetSharedSummaryQuery,
    useGetSharedFilesWithSummaryQuery,
    useAddCollectionMutation,
    useAddFilesToCollectionMutation,
    useGetDisabledCollectionsQuery,
    useGetCollectionsSuggestionsQuery,
    useDeleteFilesFromCollectionMutation,
    useGetFavoriteFilesQuery,
    useGetCollectionsSummaryQuery,
} = collection
